<template>
  <div
    class="app-layout-container"
    :class="{ 'has-sidebar': isLoggedIn, 'sidebar-open': isSidebarOpen }"
  >
    <VpnLockOverlay />
    <nav style="padding: 0px" v-if="isLoggedIn">
      <site-nav-bar-Landing
        :userName="username"
        :isSidebarOpen="isSidebarOpen"
        @toggle-sidebar="toggleSidebar"
        @close-sidebar="closeSidebar"
      />
    </nav>
    <!-- Sidebar Backdrop for mobile/tablet screen sizes -->
    <div
      v-if="isSidebarOpen && isLoggedIn"
      class="sidebar-backdrop d-lg-none"
      @click="closeSidebar"
    ></div>
    <div :class="{ 'app-content-wrapper': isLoggedIn }">
      <router-view :style="routerViewStyle" />
    </div>

    <!-- Re Login Popup -->
    <div v-if="showRelogin" class="relogin-backdrop">
      <div class="relogin-box">
        <div class="relogin-header">
          <h5>Re-Login</h5>
        </div>

        <div class="relogin-body">
          <div class="form-group row mb-3">
            <label class="col-sm-3 col-form-label">Username</label>
            <div class="col-sm-9">
              <input
                class="form-control"
                type="text"
                :value="username"
                disabled
              />
            </div>
          </div>

          <div class="form-group row mb-3">
            <label class="col-sm-3 col-form-label">Password</label>
            <div class="col-sm-9">
              <input
                class="form-control"
                type="password"
                v-model="rePassword"
                placeholder="Password"
                @keyup.enter="reLogin"
                :ref="
                  (el) => {
                    pwdInput = el;
                  }
                "
                autocomplete="current-password"
              />
              <div
                v-if="reLoginError"
                class="text-danger mt-1"
                style="font-size: 13px"
              >
                {{ reLoginError }}
              </div>
            </div>
          </div>
        </div>

        <div class="relogin-actions">
          <button
            class="btn btn-login"
            @click="reLogin"
            :disabled="reLoginLoading || !rePassword"
          >
            <span v-if="reLoginLoading">...</span>
            <span v-else>LOGIN</span>
          </button>
          <button class="btn btn-logout" @click="logout">LOG OUT</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VpnLockOverlay from "@/components/VpnLockOverlay.vue";
import SiteNavBarLogin from "@/components/SiteNavBarLogin.vue";
import SiteNavBarLanding from "@/components/SiteNavBarLanding.vue";
import apis from "@/apis";
import { useIdleTimer } from "@/composables/useIdleTimer";
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { detectClientVpn, triggerVpnLogout, clearVpnCache } from "@/utils/vpnCheck";

export default {
  components: { SiteNavBarLogin, SiteNavBarLanding, VpnLockOverlay },

  setup() {
    const store = useStore();

    // Reactive auth state
    const isAuthenticated = computed(() => store.getters.isLoggedIn);

    // Idle Timer
    const { isLocked, resetTimer } = useIdleTimer(isAuthenticated);

    // Session lock overlay state
    const showRelogin = ref(false);
    const rePassword = ref("");
    const reLoginLoading = ref(false);
    const reLoginError = ref("");
    const showPwd = ref(false);
    const pwdInput = ref(null);
    const isSidebarOpen = ref(window.innerWidth >= 992);

    // suspendSession: wipe token so API calls fail behind the lock
    function suspendSession() {
      sessionStorage.removeItem("userToken");
      sessionStorage.removeItem("isLoggedIn");
      // Keep the user object so username is visible on the lock screen
    }

    // When idle timer locks → show overlay & wipe token
    watch(isLocked, (locked) => {
      if (locked) {
        suspendSession();
        showRelogin.value = true;
        rePassword.value = "";
        reLoginError.value = "";
        showPwd.value = false;
        // Focus password field after render
        setTimeout(() => {
          if (pwdInput.value) {
            pwdInput.value.focus();
          }
        }, 100);
      }
    });

    const toggleSidebar = () => {
      isSidebarOpen.value = !isSidebarOpen.value;
    };
    const closeSidebar = () => {
      isSidebarOpen.value = false;
    };

    // --- Background VPN Monitor ---
    // Uses adaptive polling:
    //   - Every 15 seconds when user is clean (saves API calls, avoids rate limits)
    //   - Instantly when the user's network changes (catches VPN connect immediately)
    let vpnInterval = null;

    const runVpnCheck = async () => {
      if (isAuthenticated.value && !isLocked.value) {
        try {
          const vpnResult = await detectClientVpn();
          if (vpnResult && vpnResult.isVpn) {
            triggerVpnLogout(vpnResult.message || "Background Security Flag");
          }
        } catch (e) {
          // Silent — VPN check failure must never affect user experience
        }
      }
    };

    // Instant recheck when network changes (VPN connects / disconnects)
    const handleNetworkChange = () => {
      clearVpnCache(); // Force fresh IP check
      runVpnCheck();
    };

    const startVpnMonitor = () => {
      if (!vpnInterval) {
        // Adaptive: check every 15 seconds during normal use
        vpnInterval = setInterval(runVpnCheck, 15000);

        // Also react instantly on any network adapter change
        if (navigator.connection) {
          navigator.connection.addEventListener("change", handleNetworkChange);
        }
        window.addEventListener("online", handleNetworkChange);
      }
    };

    const stopVpnMonitor = () => {
      if (vpnInterval) {
        clearInterval(vpnInterval);
        vpnInterval = null;
        if (navigator.connection) {
          navigator.connection.removeEventListener("change", handleNetworkChange);
        }
        window.removeEventListener("online", handleNetworkChange);
      }
    };

    watch(isAuthenticated, (isAuth) => {
      if (isAuth) {
        startVpnMonitor();
      } else {
        stopVpnMonitor();
      }
    }, { immediate: true });
    // --- End Background VPN Monitor ---

    return {
      isAuthenticated,
      isLocked,
      resetTimer,
      showRelogin,
      rePassword,
      reLoginLoading,
      reLoginError,
      showPwd,
      pwdInput,
      isSidebarOpen,
      toggleSidebar,
      closeSidebar,
    };
  },

  computed: {
    isLoggedIn() {
      return this.$store.getters.isLoggedIn;
    },
    username() {
      const user = this.$store.getters.loggedUser;
      return user && user.data ? user.data.user_name : "";
    },
    routerViewStyle() {
      if (this.isLoggedIn) {
        return { paddingTop: "0px" };
      }
      if (this.$route.path === "/" || this.$route.path === "/LandingView") {
        // return { paddingTop: '60px' };
        return { paddingTop: "10px" };
      }
      const otherPublicPages = ["/LoginView", "/register", "/privacy-policy"];
      if (otherPublicPages.includes(this.$route.path)) {
        // return { paddingTop: '230px' };
        return { paddingTop: "0px" };
      }
      return { paddingTop: "150px" };
    },
  },

  methods: {
    async validateSession() {
      try {
        const response = await apis.getUserDetails();
        if (!response?.data?.status) {
          this.$store.dispatch("logout");
          this.$router.push("/LoginView");
        }
      } catch (error) {
        this.$store.dispatch("logout");
        this.$router.push("/LoginView");
      }
    },

    async reLogin() {
      if (!this.rePassword) return;
      this.reLoginLoading = true;
      this.reLoginError = "";
      try {
        const payload = {
          user_name: this.username,
          password: this.rePassword,
        };

        const res = await apis.loginUser(payload);

        if (res.data.status === true) {
          await this.$store.dispatch("login", { token: res.data.token });
          await this.$store.dispatch("fetchUserDetails");

          this.rePassword = "";
          this.reLoginError = "";
          this.showPwd = false;
          this.showRelogin = false;

          // Reset idle timer so the clock starts fresh
          this.resetTimer();
        } else {
          this.reLoginError =
            res.data.message || "Invalid password. Please try again.";
          this.rePassword = "";
          // pwdInput comes from setup() ref — use directly, not $refs
          this.$nextTick(() => {
            if (this.pwdInput) this.pwdInput.focus();
          });
        }
      } catch (e) {
        this.reLoginError =
          e.response?.data?.message ||
          e.response?.data?.error ||
          "Invalid password or network error.";
        this.rePassword = "";
        this.$nextTick(() => {
          if (this.pwdInput) this.pwdInput.focus();
        });
      } finally {
        this.reLoginLoading = false;
      }
    },

    initials(name) {
      return (name || "?")
        .split(" ")
        .map((n) => n[0])
        .join("")
        .slice(0, 2)
        .toUpperCase();
    },

    logout() {
      this.$store.dispatch("logout");
      this.showRelogin = false;
      this.rePassword = "";
      this.reLoginError = "";
      this.$router.push("/LoginView");
    },
  },
};
</script>

<style>
#app {
  font-family: "Plus Jakarta Sans", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  display: flex;
  flex-direction: column;
  height: 100vh;
}

nav a {
  font-weight: bold;
  color: #2c3e50;
}

nav a.router-link-exact-active {
  color: #42b983;
}

.relogin-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.relogin-box {
  background: #ffffff;
  width: 580px;
  border-radius: 4px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  animation: popupFade 0.3s ease-out;
  overflow: hidden;
}

.relogin-header {
  padding: 15px 25px;
  border-bottom: 1px solid #e0e0e0;
}

.relogin-header h5 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #444;
}

.relogin-body {
  padding: 30px 40px 10px;
}

.relogin-box .col-form-label {
  font-size: 15px;
  color: #444;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.relogin-box .form-control {
  border: 1px solid #dcdfe6;
  border-radius: 4px !important;
  font-size: 15px !important;
  height: 42px !important;
  padding-left: 15px !important;
  background-color: #fff;
  transition: border-color 0.2s;
}

.relogin-box .form-control:focus {
  border-color: #409eff;
  box-shadow: none;
}

.relogin-box .form-control:disabled {
  background-color: #f5f7fa;
  color: #909399;
}

.relogin-actions {
  display: flex;
  padding: 10px 40px 30px;
  gap: 20px;
}

.relogin-actions .btn {
  flex: 1;
  height: 46px;
  font-size: 16px;
  font-weight: 700;
  border-radius: 4px;
  color: white;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.btn-login {
  background-color: #2ecc71 !important;
}

.btn-login:hover:not(:disabled) {
  background-color: #27ae60 !important;
}

.btn-login:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-logout {
  background-color: #e11313c0 !important;
}

.btn-logout:hover {
  background-color: #ef2c2cff !important;
}

@keyframes popupFade {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Global Sidebar Layout styling */
.app-layout-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
}

.app-layout-container.has-sidebar {
  flex-direction: row;
}

.app-content-wrapper {
  position: relative;
  flex-grow: 1;
  margin-left: 0;
  width: 100%;
  min-height: 100vh;
  overflow-y: auto;
  transition: margin-left 0.3s ease, width 0.3s ease;
  display: flex;
  flex-direction: column;
  /* background-color: #f8fafc; */
  padding-top: 86px;
  padding-bottom: 50px;
}

/* Hide legacy local footers from individual dashboard views */
footer:not(.content-footer) {
  display: none !important;
}

@media (min-width: 992px) {
  .app-content-wrapper {
    margin-left: 70px;
    width: calc(100% - 70px);
  }
  .sidebar-open .app-content-wrapper {
    margin-left: 260px;
    width: calc(100% - 260px);
  }
}

.sidebar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(15, 23, 42, 0.4);
  z-index: 999;
}
</style>
